import { GetVoiceSignParams, GetVoiceSignReturnType } from "../typings";
import { UserVolume } from "../typings/live/UserVolume";
import {
  RTCJoinedInfo,
  RTCJoinedInfoCodeEnum,
} from "../typings/rtc/joinedInfo";
import { RTCType } from "../typings/rtc/type";
import { VRWebViewRTCOptions } from "../typings/rtc/VRWebViewRTCOptions";
import { CoreRTC } from "./CoreRTC";

/**
 * VR WebView RTC 实现
 * 
 * 适用于在如视 VR App 的 WebView 或微信小程序环境中进行实时语音通信。
 * 通过 JSBridge 与原生 App 通信，实现语音功能。
 * 
 * @example
 * ```typescript
 * import JSBridge from '@realsee/jsbridge-x'
 * 
 * const jsBridge = new JSBridge()
 * const rtc = new VRWebViewRTC({
 *   jsBridge,
 *   getVoiceSign: async (params) => {
 *     const response = await fetch('/api/get-voice-sign', {
 *       method: 'POST',
 *       body: JSON.stringify(params)
 *     })
 *     return response.json()
 *   }
 * })
 * 
 * // 加入房间
 * await rtc.join({
 *   voiceId: 'voice-id',
 *   userId: 'user-id',
 *   roomId: 'room-id',
 *   type: RTCType.RealseeVRWebViewRTC
 * })
 * ```
 */
export class VRWebViewRTC extends CoreRTC {
  type: RTCType = RTCType.RealseeVRWebViewRTC;
  joined = false;
  micro = false;
  voiceId = "";

  /**
   * 保留加入房间参数，用于重连
   */
  private _joinOptions: {
    voiceId: string;
    userId: string;
    roomId: string;
    type: RTCType;
  } | null = null;

  private options: VRWebViewRTCOptions;

  /**
   * 获取 JSBridge 实例
   * 
   * @returns JSBridge 协议实例
   */
  get jsBridge() {
    return this.options.jsBridge;
  }

  /**
   * 创建 VRWebViewRTC 实例
   * 
   * @param options 配置选项，包含以下属性：
   * - jsBridge: JSBridge 实例，必需，用于与原生 App 通信
   * - getVoiceSign: 获取语音签名的函数，必需
   */
  constructor({ jsBridge, getVoiceSign }: VRWebViewRTCOptions) {
    super();
    this.options = { jsBridge, getVoiceSign };

    // FIXME: 在微信小程序下有个音量上报的BUG会导致消息体过大从而jsbridge异常断开, 所以暂时不监听小程序上的音量
    if (this.jsBridge.webViewType !== "WxMiniProgramWebView") {
      this.jsBridge.callAndListen("live/userVolumes", this.handleUserVolumes);
    }

    this.jsBridge.callAndListen("live/weakNetwork", this.handleWeakNetwork);

    // hack app 上的 join 是一个 listener，所以需要在这里监听
    this.on("inner.rtc.fail", this.handleJoinResult);
  }

  /**
   * 处理用户音量（内部方法）
   * 
   * @param userVolumes 用户音量数组
   */
  private handleUserVolumes = (userVolumes: UserVolume[]) => {
    this.emit("userVolumes", userVolumes);
  };

  /**
   * 处理弱网络情况（内部方法）
   * 
   * @param weakNetwork 是否为弱网络
   */
  private handleWeakNetwork = (weakNetwork: boolean) => {
    if (weakNetwork) this.emit("weakNetwork");
  };

  /**
   * 加入语音房间
   * 
   * @param options 加入房间的选项
   * @param options.voiceId 语音房间ID
   * @param options.userId 用户ID
   * @param options.roomId 房间ID
   * @param options.type RTC类型
   * @returns Promise<boolean> 是否成功加入房间
   */
  join = async (options: {
    voiceId: string;
    userId: string;
    roomId: string;
    type: RTCType;
  }) => {
    // 保存加入房间参数，用于重连
    this._joinOptions = options;

    // 触发将要加入房间事件
    this.emit("joinWillStart");

    // 获取 rtc 签名
    const { sdkAppId, userId, userSig } = await this.getVoiceSignOrEmitError(
      options
    );
    // 如果获取 rtc 签名失败，则直接返回
    if (!sdkAppId || !userId || !userSig) return false;

    // 保存 rtc 类型
    this.type = options.type;

    // 对小程序进行处理
    return this.jsBridge.webViewType === "WxMiniProgramWebView"
      ? this.handleWxMiniProgramWebViewJoin({
          sdkAppId,
          userId,
          userSig,
          voiceId: options.voiceId,
        })
      : this.handleAppWebViewJoin({
          sdkAppId,
          userId,
          userSig,
          voiceId: options.voiceId,
        });
  };

  /**
   * 重连
   * @returns
   */
  reJoin = async () => {
    if (!this._joinOptions) {
      this.emitErrorAndSetJoinedFalse("重连失败：未找到加入房间参数。");
      return false;
    }

    return this.join({
      voiceId: this._joinOptions.voiceId,
      userId: this._joinOptions.userId,
      roomId: this._joinOptions.roomId,
      type: this._joinOptions.type,
    });
  }

  /**
   * 获取 rtc 签名
   * @param params
   * @returns
   */
  private getVoiceSignOrEmitError = async (
    params: GetVoiceSignParams
  ): Promise<GetVoiceSignReturnType> => {
    try {
      return await this.options.getVoiceSign(params);
    } catch (e) {
      this.emitErrorAndSetJoinedFalse("加入房间失败：rtc 签名请求异常");
      return {} as GetVoiceSignReturnType;
    }
  };

  /**
   * 处理小程序的加入房间
   * @param options
   * @returns
   */
  private handleWxMiniProgramWebViewJoin = async (options: {
    sdkAppId: number;
    userId: string;
    userSig: string;
    voiceId: string;
  }) => {
    const [res, error] = await this.joinRoomOrEmitError(options);
    if (error) return false;
    return this.handleJoinResult(res as RTCJoinedInfo | boolean);
  };

  /**
   * 处理App WebView的加入房间
   * @param options
   * @returns
   */
  private handleAppWebViewJoin = (options: {
    sdkAppId: number;
    userId: string;
    userSig: string;
    voiceId: string;
  }) => {
    return new Promise<boolean>((resolve) => {
      // 先解除之前的内部 rtc 监听
      this.off("inner.rtc.success");

      const callback = (res: RTCJoinedInfo | boolean) => {
        this.off("inner.rtc.fail", callback);
        this.off("inner.rtc.success", callback);
        resolve(this.handleJoinResult(res));
      };

      this.once("inner.rtc.fail", callback);
      this.once("inner.rtc.success", callback);

      try {
        const { sdkAppId, userId, userSig, voiceId } = options;
        this.jsBridge.callAndListen<boolean>(
          `live/join?sdkAppID=${sdkAppId}&userID=${userId}&roomID=${voiceId}&userSig=${userSig}`,
          (res: boolean | RTCJoinedInfo) => {
            if (res === true) {
              this.emit("inner.rtc.success", res);
            } else if (typeof res === "object") {
              if (
                res.code === RTCJoinedInfoCodeEnum.Success ||
                res.code === RTCJoinedInfoCodeEnum.ReJoin
              ) {
                this.emit("inner.rtc.success", res);
              } else {
                this.emit("inner.rtc.fail", res);
              }
            } else {
              this.emit("inner.rtc.fail", res);
            }
          }
        );
      } catch (e) {
        resolve(
          this.handleJoinResult({
            message: (e as Error).message,
            code: RTCJoinedInfoCodeEnum.Unknown,
          })
        );
      }
    });
  };

  /**
   * 加入语音房间，给小程序使用
   * @param param0
   * @returns
   */
  private joinRoomOrEmitError = async ({
    sdkAppId,
    userId,
    voiceId,
    userSig,
  }: {
    sdkAppId: number;
    userId: string;
    voiceId: string;
    userSig: string;
  }): Promise<[boolean | RTCJoinedInfo | null, Error | null]> => {
    try {
      return await this.jsBridge.callAndBackfeed<RTCJoinedInfo | true>(
        `live/join?sdkAppID=${sdkAppId}&userID=${userId}&roomID=${Number(
          voiceId
        )}&userSig=${userSig}`
      );
    } catch (e) {
      this.emitErrorAndSetJoinedFalse("加入房间失败：" + (e as Error).message);
      return [null, e] as [null, Error];
    }
  };

  /**
   * 处理加入语音房间结果
   * @param res
   * @returns
   */
  private handleJoinResult = (res: RTCJoinedInfo | boolean) => {
    switch (true) {
      case res === true:
      case typeof res === "object" &&
        res.code === RTCJoinedInfoCodeEnum.Success:
      case typeof res === "object" &&
        res.code === RTCJoinedInfoCodeEnum.ReJoin: {
        this.emit("joined");
        this.joined = true;
        break;
      }
      default: {
        this.joined = false;
        const message =
          typeof res === "object" ? res.message : "加入房间失败：未知异常。";
        this.emit("error", new Error(message, { cause: res }));
      }
    }

    return this.joined;
  };

  /**
   * 将错误信息发送出去，并将 joined 置为 false
   * @param message
   */
  private emitErrorAndSetJoinedFalse = (message: string) => {
    this.emit("error", new Error(message));
    this.joined = false;
  };

  /**
   * 退出房间
   * @returns
   */
  quit = async () => {
    const [res, error] = await this.jsBridge.callAndBackfeed<boolean>(
      "live/quit"
    );
    if (res === null) {
      this.emit("error", error ? error : new Error("退出房间失败：未知异常。"));
    }
    this.joined = false;
    return !!res;
  };

  /**
   * 监测麦克风权限
   * @returns
   */
  detectMicro = async () => {
    const [res, error] = await this.jsBridge.detectMicro();
    if (res === null) {
      this.emit("error", error ? error : new Error("麦克风监测异常！"));
      return false;
    }
    return res;
  };

  /**
   * 修改麦克风状态
   * @param flag
   * @returns
   */
  toggleMicro = async (flag?: boolean) => {
    const [res, error] = await this.jsBridge.callAndBackfeed<boolean>(
      `live/toggleMicro?flag=${Boolean(flag) ? 1 : 0}`
    );
    if (res === null) {
      this.emit("error", error ? error : new Error("麦克风监测异常！"));
      return false;
    }
    this.micro = res;
    return res;
  };

  /**
   * 震动
   */
  shock = () => {
    this.jsBridge.shock(1);
  };
}
